import * as UTIF from 'utif2';

/**
 * Skan w TIFF (03.11.2026, `preOrderDocuments`): przeglądarki poza Safari
 * nie pokazują TIFF-a, a skanery i faksy zapisują w nim wielostronicowe
 * zamówienia. Dekodujemy go sami — biblioteka `utif2` (UTIF.js, MIT, bez
 * zależności poza `pako`) — strona po stronie do obrazu PNG pokazywanego
 * w `<img>`. Nic się nie wykonuje: to tylko piksele.
 *
 * Strony dekodujemy dopiero przy wyświetleniu (wielostronicowy skan 50 MB
 * nie zajmuje pamięci naraz), a stronę większą niż limit pikseli pomijamy
 * z powodem zamiast zawiesić kartę.
 */

/** Największa strona do zdekodowania w przeglądarce (≈ A4 w 600 dpi z zapasem). */
export const MAX_PAGE_PIXELS = 60_000_000;

export interface TiffDocument {
  buffer: ArrayBuffer;
  /** Katalogi obrazów (strony) z wymiarami; miniatury EXIF pominięte. */
  pages: UTIF.IFD[];
}

/** Strony skanu. Plik, który nie jest TIFF-em, rzuca wyjątek. */
export function readTiff(buffer: ArrayBuffer): TiffDocument {
  const ifds = UTIF.decode(buffer);
  const pages = ifds.filter((ifd) => {
    const w = Number((ifd.t256 as number[] | undefined)?.[0] ?? ifd.width ?? 0);
    const h = Number((ifd.t257 as number[] | undefined)?.[0] ?? ifd.height ?? 0);
    return w > 0 && h > 0;
  });
  return { buffer, pages };
}

/** Wymiary strony z nagłówka (bez dekodowania pikseli). */
export function pageSize(ifd: UTIF.IFD): { width: number; height: number } {
  return {
    width: Number((ifd.t256 as number[] | undefined)?.[0] ?? ifd.width ?? 0),
    height: Number((ifd.t257 as number[] | undefined)?.[0] ?? ifd.height ?? 0),
  };
}

/** Strona jako obraz PNG (`data:`); za duża — `null`. */
export function renderTiffPage(doc: TiffDocument, index: number): string | null {
  const ifd = doc.pages[index];
  if (!ifd) return null;
  const { width, height } = pageSize(ifd);
  if (width * height > MAX_PAGE_PIXELS) return null;
  UTIF.decodeImage(doc.buffer, ifd);
  const rgba = UTIF.toRGBA8(ifd);
  const canvas = document.createElement('canvas');
  canvas.width = ifd.width;
  canvas.height = ifd.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const image = ctx.createImageData(ifd.width, ifd.height);
  image.data.set(rgba);
  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL('image/png');
}
